import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Link as ChakraLink,
  useColorModeValue,
  Badge,
  SimpleGrid,
  Icon,
} from '@chakra-ui/react'
import { FiDollarSign, FiShield, FiLayers, FiRefreshCw } from 'react-icons/fi'
import Balancer from 'react-wrap-balancer'
import { useAccount } from 'wagmi'

import { ConnectWallet } from '@/components'
import { useIsClient } from '@/hooks'

const TokenizedAssets = () => {
  const isClient = useIsClient()
  const { isConnected } = useAccount()
  const patentTextColor = useColorModeValue('slate.600', 'slate.400')
  const patentLinkColor = useColorModeValue('brand.700', 'brand.300')
  const cardBg = useColorModeValue('white', 'slate.800')
  const cardBorder = useColorModeValue('slate.200', 'slate.700')

  if (!isConnected && isClient) {
    return <ConnectWallet />
  }

  return (
    <Box w="full">
      <Flex
        textAlign="center"
        flexDir="column"
        justifyContent="center"
        alignItems="center"
        my="12"
        mx="auto"
        maxW="xl"
      >
        <Badge colorScheme="green" fontSize="sm" px={3} py={1} mb={4}>
          Tokenized Assets
        </Badge>
        <Heading mt="2" zIndex="1">
          <Balancer>Tokenized Real-World Asset Protection</Balancer>
        </Heading>
        <Text mt="3" zIndex="1" color="slate.600">
          <Balancer>
            Cryptographically-bound surrogate tokens that secure tokenized representations of
            real-world assets — including treasuries, equities, and yield-bearing instruments —
            by providing immutable proof of ownership and enabling controlled transferability
            without compromising the underlying asset.
          </Balancer>
        </Text>
        <Text fontSize="xs" color={patentTextColor} mt="2" zIndex="1">
          Protected by{' '}
          <ChakraLink
            href="/patents"
            color={patentLinkColor}
            textDecoration="none"
            _hover={{ textDecoration: 'underline' }}
          >
            U.S. Patent No. 12,293,358
          </ChakraLink>
        </Text>
      </Flex>

      <Container maxW="4xl" my={{ base: 8, md: 12 }}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <FeatureCard
            icon={FiDollarSign}
            title="Treasury & Yield Token Binding"
            description="Surrogate tokens cryptographically bound to tokenized treasury positions and yield-bearing instruments, ensuring verifiable proof of ownership on-chain."
            cardBg={cardBg}
            cardBorder={cardBorder}
          />
          <FeatureCard
            icon={FiShield}
            title="Asset Integrity Protection"
            description="Underlying real-world asset representations remain secured while surrogate tokens enable controlled interaction, preventing unauthorized transfers or manipulation."
            cardBg={cardBg}
            cardBorder={cardBorder}
          />
          <FeatureCard
            icon={FiLayers}
            title="Multi-Chain RWA Verification"
            description="Prove ownership of tokenized real-world assets across multiple blockchain networks without migrating the underlying position or relying on third-party bridges."
            cardBg={cardBg}
            cardBorder={cardBorder}
          />
          <FeatureCard
            icon={FiRefreshCw}
            title="Compliant Transferability"
            description="Enable controlled transfer and redemption workflows for tokenized securities and funds while maintaining compliance verification through on-chain surrogate token status."
            cardBg={cardBg}
            cardBorder={cardBorder}
          />
        </SimpleGrid>
      </Container>

      <Container maxW="4xl" mb={12}>
        <Box
          p={8}
          borderWidth="1px"
          borderRadius="lg"
          borderColor={cardBorder}
          bg={cardBg}
          textAlign="center"
        >
          <Text fontSize="lg" fontWeight="medium">
            Contact{' '}
            <ChakraLink
              href="mailto:sales@foolproof.xyz"
              color={patentLinkColor}
              _hover={{ textDecoration: 'underline' }}
            >
              sales@foolproof.xyz
            </ChakraLink>
            {' '}to get started
          </Text>
        </Box>
      </Container>
    </Box>
  )
}

interface FeatureCardProps {
  icon: React.ElementType
  title: string
  description: string
  cardBg: string
  cardBorder: string
}

const FeatureCard = ({ icon, title, description, cardBg, cardBorder }: FeatureCardProps) => {
  return (
    <Box p={6} borderWidth="1px" borderRadius="lg" borderColor={cardBorder} bg={cardBg}>
      <Flex align="center" mb={3}>
        <Icon as={icon} boxSize={6} color="brand.500" mr={3} />
        <Heading size="sm">{title}</Heading>
      </Flex>
      <Text fontSize="sm" color="slate.600">
        {description}
      </Text>
    </Box>
  )
}

export default TokenizedAssets
